import { assertSafeInteger, type Fixed, fixedScaleByInteger, portionBps } from '@kenocyber/shared';

/**
 * Offline farm - decided rules:
 *
 * - Capped at 6 hours of credited time.
 * - Grants 70% of the estimated XP and Gold.
 * - Never grants items.
 * - Computed by the SERVER when the player returns, from server timestamps.
 */

export const OFFLINE_FARM_CAP_SECONDS = 6 * 60 * 60;
export const OFFLINE_FARM_EFFICIENCY_BPS = 7000; // 70%

export interface OfflineFarmInput {
  /** Server timestamp of the last credited activity, epoch seconds. */
  readonly lastSeenEpochSeconds: number;
  /** Server timestamp of the return, epoch seconds. NEVER the client clock. */
  readonly nowEpochSeconds: number;
  /** Estimated XP per second at the player's current setup. */
  readonly xpPerSecond: Fixed;
  /** Estimated Gold per second at the player's current setup. */
  readonly goldPerSecond: Fixed;
}

export interface OfflineFarmResult {
  /** Seconds actually credited after applying the cap. */
  readonly creditedSeconds: number;
  readonly xpGained: Fixed;
  readonly goldGained: Fixed;
  /** Items are never granted offline. Kept explicit so no caller "forgets". */
  readonly itemsGained: 0;
}

export function computeOfflineFarm(input: OfflineFarmInput): OfflineFarmResult {
  const { lastSeenEpochSeconds, nowEpochSeconds } = input;
  // Whole seconds only: a fractional or NaN timestamp is a bug upstream, and
  // credited income must never be computed from a coerced value.
  assertSafeInteger(lastSeenEpochSeconds, 'lastSeenEpochSeconds');
  assertSafeInteger(nowEpochSeconds, 'nowEpochSeconds');
  const elapsed = Math.max(0, nowEpochSeconds - lastSeenEpochSeconds);
  const creditedSeconds = Math.min(elapsed, OFFLINE_FARM_CAP_SECONDS);

  const fullXp = fixedScaleByInteger(input.xpPerSecond, creditedSeconds);
  const fullGold = fixedScaleByInteger(input.goldPerSecond, creditedSeconds);

  return {
    creditedSeconds,
    xpGained: portionBps(fullXp, OFFLINE_FARM_EFFICIENCY_BPS),
    goldGained: portionBps(fullGold, OFFLINE_FARM_EFFICIENCY_BPS),
    itemsGained: 0,
  };
}
